import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import Select from 'react-select';
import MyEditor from '../../../Components/editor/MyEditor';
import tmp_img from "../../../assets/images/voucher_tmp/부산-logo.png";
import moment from 'moment';
import { API } from '../../../api/client';
import { Button } from '@/Components/ui';
import { dialogService } from "@/utils/dialogService";
import { ticketService } from '@/api/services/ticket.service';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import type { TicketProductApiItem, TicketProductOption, TicketProductSummary } from '@/api/types/ticket';
import {
    isAmazingPassTicket,
    mergeTicketProductOptions,
    toTicketProductOption,
} from '@/pages/Tickets/TicketProducts/ticketProductForm.shared';
import AmazingPassTicketCreateModal from './AmazingPassTicketCreateModal';

type PackageOption = {
    value: number;
    label: string;
};

type RefundPolicy = "each" | "all_unused";
type DateInputValue = string | Date | Date[] | null | undefined;

const padDatePart = (value: number) => String(value).padStart(2, "0");

const formatLocalDate = (date: Date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
    }

    return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const normalizeDateValue = (dateObj: DateInputValue) => {
    if (!dateObj) {
        return "";
    }

    const value = Array.isArray(dateObj) ? dateObj[0] : dateObj;

    if (!value) {
        return "";
    }

    if (value instanceof Date) {
        return formatLocalDate(value);
    }

    const matchedDatePrefix = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (matchedDatePrefix) {
        const [, year, month, day] = matchedDatePrefix;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const normalizeExpireDateTime = (dateObj: DateInputValue) => {
    const normalizedDate = normalizeDateValue(dateObj);
    return normalizedDate ? `${normalizedDate}T00:00:00` : "";
};

const PreCouponForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCopyMode = searchParams.get("copy") === "true";
    const requestedTab = searchParams.get("tab");
    const fileInputRef = React.useRef(null);
    const salesSettingsRef = React.useRef<HTMLDivElement | null>(null);
    const [name, setName] = React.useState("");
    const [expireDate, setExpireDate] = React.useState<DateInputValue>(null);
    const [description, setDescription] = React.useState("");
    const [autoSend, setAutoSend] = React.useState(false);
    const [autoSendDate, setAutoSendDate] = React.useState(null);
    const [imageUrl, setImageUrl] = React.useState(null);
    const [image, setImage] = React.useState(null);
    const [reservStartDate, setReservStartDate] = React.useState<DateInputValue>(null);
    const [reservEndDate, setReservEndDate] = React.useState<DateInputValue>(null);
    const [discount, setDiscount] = React.useState(0);
    const [count, setCount] = React.useState(0);
    const [packageOptions, setPackageOptions] = React.useState<PackageOption[]>([]);
    const [packages, setPackages] = React.useState<PackageOption[]>([]);
    const [isActive, setIsActive] = React.useState(true);
    const [ticketOptions, setTicketOptions] = React.useState<TicketProductOption[]>([]);
    const [amazingPassTicketId, setAmazingPassTicketId] = React.useState<number | "">("");
    const [amazingPassBundleCount, setAmazingPassBundleCount] = React.useState(1);
    const [refundPolicy, setRefundPolicy] = React.useState<RefundPolicy>("each");
    const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const markDirty = React.useCallback(() => setHasUnsavedChanges(true), []);
    const { confirmDiscardChanges } = useUnsavedChangesGuard({
        isDirty: hasUnsavedChanges,
        confirmNavigation: dialogService.confirm,
    });
    const selectedAmazingPassTicket = React.useMemo(() => {
        return ticketOptions.find((option) => option.value === amazingPassTicketId) || null;
    }, [amazingPassTicketId, ticketOptions]);
    const selectedTicketBundleCount = selectedAmazingPassTicket?.bundle_count || 1;
    const expectedIssuedCount = amazingPassTicketId ? selectedTicketBundleCount * amazingPassBundleCount : 0;
    const fetchPackageOptions = () => {
        API.get('/package/items/')
            .then((response) => {
            let tmpOptions: PackageOption[] = [];
            response.data.forEach((item) => {
                tmpOptions.push({
                    value: item.id,
                    label: item["상품명"]
                });
            });
            setPackageOptions(tmpOptions);
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    };
    const fetchTicketOptions = React.useCallback(() => {
        ticketService.getTicketProducts()
            .then((response) => {
            const options = (response.data || [])
                .filter((item: TicketProductApiItem) => isAmazingPassTicket(item))
                .map((item: TicketProductApiItem) => toTicketProductOption(item));
            setTicketOptions((current) => mergeTicketProductOptions(current, options));
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    }, []);
    const handleImageFiles = (e) => {
        const files = e.target.files;
        if (files.length !== 0) {
            markDirty();
            setImage(files[0]);
        }
    };
    React.useEffect(() => {
        if (id !== '0' && packageOptions.length > 0) {
            API.get(`/precoupon/precoupon/${id}`)
                .then(response => fillStates(response.data, isCopyMode))
                .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
        }
    }, [id, packageOptions, isCopyMode]);
    const fillStates = (data, copyMode = false) => {
        console.log("fillStates", data);
        setName(copyMode ? data["name"] + " (복사)" : data["name"]);
        setExpireDate(data["expire_date"]);
        setDescription(data["description"]);
        setAutoSend(data["auto_send"]);
        setAutoSendDate(data["auto_send_date"]);
        if (!copyMode) {
            setImageUrl(data["image"]);
        }
        setReservStartDate(data["reserv_start_date"]);
        setReservEndDate(data["reserv_end_date"]);
        setDiscount(data["discount"]);
        setCount(data["count"]);
        setAmazingPassTicketId(data["amazingpass_ticket"] || "");
        setAmazingPassBundleCount(data["amazingpass_bundle_count"] || 1);
        setRefundPolicy(data["refund_policy"] || "each");
        if (data["available_packages"]) {
            let tmpPackages = data["available_packages"]
                .filter((item) => !!packageOptions.find((option) => option.value === item))
                .map((item) => {
                const _item = packageOptions.find((option) => option.value === item);
                if (_item)
                    return {
                        value: _item.value,
                        label: _item.label
                    };
            });
            console.log("tmpPackages", tmpPackages);
            setPackages(tmpPackages);
        }
        setIsActive(data["is_active"]);
    };
    const sendData = async () => {
        const [formData, jsonData] = await checkData();
        if (formData === null)
            return;
        API.post('/precoupon/precoupon/', formData, {
            headers: { "content-type": "multipart/form-data" }
        })
            .then((res) => {
            return API.patch(`/precoupon/precoupon/${res.data.id}/`, jsonData);
        })
            .then(async () => {
            setHasUnsavedChanges(false);
            await dialogService.handleAlert("등록이 완료되었습니다");
            navigate('/precoupon-list');
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    };
    const updateData = async () => {
        const [formData, jsonData] = await checkData();
        if (formData === null)
            return;
        API.patch(`/precoupon/precoupon/${id}/`, formData, {
            headers: { "content-type": "multipart/form-data" }
        })
            .then(() => {
            return API.patch(`/precoupon/precoupon/${id}/`, jsonData);
        })
            .then(async () => {
            setHasUnsavedChanges(false);
            await dialogService.handleAlert("수정이 완료되었습니다");
            navigate('/precoupon-list');
        })
            .catch(async (error) => await dialogService.handleAlert(error.message, { force: "error" }));
    };
    const checkData = async () => {
        if (!name || !expireDate || !description || !reservStartDate || !reservEndDate || !packages.length) {
            await dialogService.handleAlert("필수 입력값이 없습니다.");
            return [null, null];
        }
        const normalizedExpireDateTime = normalizeExpireDateTime(expireDate);
        const normalizedReservStartDate = normalizeDateValue(reservStartDate);
        const normalizedReservEndDate = normalizeDateValue(reservEndDate);
        if (!normalizedExpireDateTime || !normalizedReservStartDate || !normalizedReservEndDate) {
            await dialogService.handleAlert("날짜 형식이 올바르지 않습니다.");
            return [null, null];
        }
        let data = new FormData();
        data.append("name", name);
        if (image)
            data.append("image", (image === null) ? new File([tmp_img], "camp-logo.png", { type: `image/png` }) : image);
        data.append("description", description);
        data.append("expire_date", normalizedExpireDateTime);
        data.append("reserv_start_date", normalizedReservStartDate);
        data.append("reserv_end_date", normalizedReservEndDate);
        data.append("discount", String(discount));
        data.append("count", String(count));
        data.append("is_active", String(isActive));
        const jsondata = {
            name: name,
            description: description,
            expire_date: normalizedExpireDateTime,
            reserv_start_date: normalizedReservStartDate,
            reserv_end_date: normalizedReservEndDate,
            discount: discount,
            count: count ? count : 0,
            is_active: isActive,
            available_packages: packages.map((item) => item.value),
            amazingpass_ticket: amazingPassTicketId || null,
            amazingpass_bundle_count: amazingPassTicketId ? amazingPassBundleCount : 0,
            refund_policy: amazingPassTicketId ? refundPolicy : "each"
        };
        return [data, jsondata];
    };
    React.useEffect(() => {
        fetchPackageOptions();
        fetchTicketOptions();
    }, [fetchTicketOptions]);
    React.useEffect(() => {
        if (!amazingPassTicketId) {
            return;
        }
        if (ticketOptions.some((option) => option.value === amazingPassTicketId)) {
            return;
        }
        ticketService.getTicketProductById(String(amazingPassTicketId))
            .then((response) => {
            if (!response.data?.id || !response.data?.name) {
                return;
            }
            setTicketOptions((current) => mergeTicketProductOptions(current, [toTicketProductOption(response.data)]));
        })
            .catch(() => undefined);
    }, [amazingPassTicketId, ticketOptions]);
    React.useEffect(() => {
        if (requestedTab === "sales" && salesSettingsRef.current) {
            salesSettingsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [requestedTab]);
    const handleCreatedAmazingPassTicket = React.useCallback((ticket: TicketProductSummary) => {
        const createdOption = toTicketProductOption(ticket);
        setTicketOptions((current) => mergeTicketProductOptions(current, [createdOption]));
        markDirty();
        setAmazingPassTicketId(ticket.id);
        setAmazingPassBundleCount((current) => current > 0 ? current : 1);
        setIsCreateTicketModalOpen(false);
        fetchTicketOptions();
    }, [fetchTicketOptions, markDirty]);
    return (<React.Fragment>
            <div className="page-content">
                <div className="container-fluid" onChangeCapture={markDirty} onInputCapture={markDirty}>
                    <BreadCrumb title="예약권 등록 및 수정" pageTitle="예약권"/>

                    <div className="flex flex-wrap -mx-3 gap-y-4 mb-3">
                        <div className="col-sm-auto px-3">
                            <div>
                                <h4 className="card-title mb-0 flex-grow-1">등록 및 수정</h4>
                            </div>
                        </div>
                        <div className="col-sm-3 ms-auto px-3">
                            <div className="d-flex justify-content-sm-end gap-2">
                                {(id !== '0' && !isCopyMode) ?
            <Button onClick={updateData} id="create-btn" color="success" className="add-btn me-1">
                                            수정
                                        </Button> :
            <Button onClick={sendData} id="update-btn" color="success" className="add-btn me-1">
                                            생성
                                        </Button>}
                                <Button onClick={async () => {
                                    if (!await confirmDiscardChanges())
                                        return;
                                    navigate(`/precoupon-list`);
                                }} id="cancel-btn" color="danger" className="add-btn me-1">
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap -mx-3">
                        <div className="w-full px-3">
                            <div className="card">
                                <div className="card-body">
                                    <div className="live-preview">
                                        <div className="flex flex-wrap -mx-3">
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="firstNameinput" className="form-label">예약권명</label>
                                                    <input type="text" className="form-control" placeholder="예약권명" id="name" value={name} onChange={(e) => (setName(e.target.value))} maxLength={50}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                {imageUrl ? <div>
                                                    <img src={imageUrl} alt="예약권이미지" width="100%"/>
                                                </div> : null}
                                                <div>
                                                    <label htmlFor="formFile" className="form-label">예약권이미지</label>
                                                    <input className="form-control" type="file" id="formFile" accept="image/jpg, image/jpeg, image/png" ref={fileInputRef} onChange={handleImageFiles}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <label htmlFor="ForminputState" className="form-label">유효기간</label>
                                                <CommonDatePicker value={expireDate} onChangeValue={(value) => {
            markDirty();
            setExpireDate(value);
        }} className="form-control" options={{
        }}/>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-3">
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formStartDate" className="form-label">예약가능일</label>
                                                    <CommonDatePicker value={reservStartDate} onChangeValue={(value) => {
            markDirty();
            setReservStartDate(value);
        }} className="form-control" options={{
        }}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formEndDate" className="form-label">예약마감일</label>
                                                    <CommonDatePicker value={reservEndDate} onChangeValue={(value) => {
            markDirty();
            setReservEndDate(value);
        }} className="form-control" options={{
        }}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formIsActive" className="form-label">사용여부</label>
                                                    <div className="form-check form-switch">
                                                        <input type="checkbox" className="form-check-input" id="formIsActive" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}/>
                                                        <label className="form-check-label" htmlFor="formIsActive">
                                                            {isActive ? "사용" : "미사용"}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-3">
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formDiscount" className="form-label">할인금액</label>
                                                    <input type="number" className="form-control" placeholder="할인금액" id="discount" value={discount} onChange={(e) => (setDiscount(Number(e.target.value)))}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formCount" className="form-label">수량</label>
                                                    <input type="number" className="form-control" placeholder="수량" id="count" value={count} onChange={(e) => (setCount(Number(e.target.value)))}/>
                                                </div>
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <div className="mb-3">
                                                    <label htmlFor="formCount" className="form-label">사용가능 패키지</label>
                                                    <Select isMulti name="packages" options={packageOptions} className="basic-multi-select" classNamePrefix="select" onChange={(options) => {
            markDirty();
            setPackages([...(options || [])]);
        }} value={packages}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-3">
                        <div className="w-full px-3">
                            <div className="card">
                                <div className="card-header">
                                    <h4 className="card-title mb-0">상세설명</h4>
                                </div>
                                <div className="card-body">
                                    <div>
                                        <MyEditor value={description} onChange={(value) => {
                                            markDirty();
                                            setDescription(value);
                                        }} noImage={true}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-3" ref={salesSettingsRef}>
                        <div className="w-full px-3">
                            <div className="card">
                                <div className="card-header">
                                    <h4 className="card-title mb-0">판매설정</h4>
                                </div>
                                <div className="card-body">
                                    <div className="flex flex-wrap -mx-3">
                                        <div className="w-full md:w-1/3 px-3">
                                            <div className="mb-3">
                                                <div className="mb-2 flex items-center justify-between gap-2">
                                                    <label htmlFor="amazingPassTicket" className="form-label mb-0">연결 어메이징패스 티켓</label>
                                                    <Button color="light" className="shrink-0" onClick={() => setIsCreateTicketModalOpen(true)}>
                                                        새 티켓 생성
                                                    </Button>
                                                </div>
                                                <Select id="amazingPassTicket" name="amazingPassTicket" options={ticketOptions} className="basic-select" classNamePrefix="select" isClearable onChange={(option) => {
                                                    markDirty();
                                                    setAmazingPassTicketId(option ? option.value : "");
                                                }} value={ticketOptions.find((option) => option.value === amazingPassTicketId) || null}/>
                                                <p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>어메이징패스 티켓만 연결할 수 있으며, 필요 시 이 화면에서 생성 후 바로 연결할 수 있습니다.</p>
                                                {selectedAmazingPassTicket ? (<p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>선택 티켓의 티켓수: {selectedTicketBundleCount}개</p>) : null}
                                                {selectedAmazingPassTicket && selectedTicketBundleCount !== 1 ? (<p className="text-danger mt-2 mb-0" style={{ fontSize: "12px" }}>예약권 연결 티켓은 티켓수를 1개로 두는 것을 권장합니다. 현재 설정은 예약권 발급 수량과 곱해져 과다 발급될 수 있습니다.</p>) : null}
                                            </div>
                                        </div>
                                        <div className="w-full md:w-1/3 px-3">
                                            <div className="mb-3">
                                                <label htmlFor="amazingPassBundleCount" className="form-label">예약권 발급 수량</label>
                                                <input type="number" min={1} className="form-control" id="amazingPassBundleCount" value={amazingPassBundleCount} disabled={!amazingPassTicketId} onChange={(e) => setAmazingPassBundleCount(Number(e.target.value) || 1)}/>
                                                <p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>연결 티켓 1장당 발급할 예약권 수입니다. 실제 발급 티켓/예약권 수: {expectedIssuedCount || 0}개</p>
                                            </div>
                                        </div>
                                        <div className="w-full md:w-1/3 px-3">
                                            <div className="mb-3">
                                                <label htmlFor="refundPolicy" className="form-label">예약권 취소 방식</label>
                                                <select id="refundPolicy" className="form-select" value={refundPolicy} disabled={!amazingPassTicketId} onChange={(e) => setRefundPolicy(e.target.value as RefundPolicy)}>
                                                    <option value="each">예약권별 개별 취소</option>
                                                    <option value="all_unused">전체 미사용일 때만 전체 취소</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AmazingPassTicketCreateModal isOpen={isCreateTicketModalOpen} onClose={() => setIsCreateTicketModalOpen(false)} onCreated={handleCreatedAmazingPassTicket}/>
        </React.Fragment>);
};
export default PreCouponForm;
